import './kitStyles';
/**
 * WEB-3310 юнит «слои в кабинете» — чей корень `.ds` судит узел служебных слоёв кита
 * (`TipLayer`, `TouchRules`, `FogRules`, `PhoneRules`). Одно правило на четыре слоя.
 *  - Режим «свой» (по умолчанию; витрина `/kit`): слой стоит внутри корня `.ds` и судит
 *    только его — корень = `метка.closest('.ds')`, узел его, если `root.contains(узел)`;
 *    у телефонного слоя (`nearest`) — если ближайший `.ds` узла и есть этот корень
 *    (вложенный корень судит сам). Метка вне `.ds` — слой не судит ничего.
 *  - Режим «все» (`everyRoot`; оболочка кабинета `DashboardLayout`): слой стоит вне `.ds`,
 *    корень узла = `узел.closest('.ds')`; узел без предка `.ds` (страницы antd) — никто.
 *    Наблюдатели изменений — на `document.body`.
 */
export const EVERY_ROOT = 'every' as const;

/** Режим слоя: «все корни» либо свой корень с правилом «содержит» / «ближайший». */
export type LayerMode =
  typeof EVERY_ROOT | { root: HTMLElement; nearest: boolean };

const nearestDs = (node: Node | null | undefined): HTMLElement | null => {
  const el = node instanceof Element ? node : node?.parentElement;
  return el ? el.closest<HTMLElement>('.ds') : null;
};

/** Режим по метке слоя и признаку; `null` — слою нечего судить (свой режим, метка вне `.ds`). */
export function layerMode(
  mark: Element | null | undefined,
  everyRoot: boolean,
  nearest = false,
): LayerMode | null {
  if (everyRoot) return EVERY_ROOT;
  const root = mark ? mark.closest<HTMLElement>('.ds') : null;
  return root ? { root, nearest } : null;
}

/** Корень `.ds`, который судит узел в этом режиме; `null` — узел не судится. */
export function layerRoot(
  node: Node | null | undefined,
  mode: LayerMode,
): HTMLElement | null {
  if (mode === EVERY_ROOT) return nearestDs(node);
  if (!node) return null;
  if (mode.nearest) return nearestDs(node) === mode.root ? mode.root : null;
  return mode.root.contains(node) ? mode.root : null;
}

/** Где слушать изменения дерева: свой корень либо `document.body`. */
export function layerBase(mode: LayerMode): HTMLElement {
  return mode === EVERY_ROOT ? document.body : mode.root;
}

/** Узлы по селектору, которые судит этот режим (порядок документа). */
export function layerAll(mode: LayerMode, sel: string): HTMLElement[] {
  return Array.from(layerBase(mode).querySelectorAll<HTMLElement>(sel)).filter(
    (el) => layerRoot(el, mode),
  );
}
